import classNames from 'classnames';
import { observer, Provider } from 'mobx-react';
import React, { useEffect } from 'react';
import { BrowseCourseCard, BrowseCourseCardContext } from '@udemy/browse-course';
import { BrowseNavigationItemSelected, BrowseNavigationItemStyle, BrowseNavigationItemType } from '@udemy/browse-event-tracking';
import { Tracker } from '@udemy/event-tracking';
import { FunnelLogContextProvider } from '@udemy/funnel-tracking';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { Button, Image } from '@udemy/react-core-components';
import { Modal } from '@udemy/react-dialog-components';
import { ContentArea, ContentGrid, ContentGridItemFullWidth } from '@udemy/react-layout-components';
import { AlertBanner } from '@udemy/react-messaging-components';
import { LinkGroup } from '@udemy/react-structure-components';
import { serverOrClient } from '@udemy/shared-utils';
import { urls } from '../add-to-cart/config';
import styles from "./cart-success-modal.module.css";
import { RelatedLabelsStore } from './related-labels.mobx-store';
export const CourseAddedMessage = ({
  onClick,
  title,
  instructors,
  image
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement("div", {
    className: styles['course-added']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['added-context']
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "success",
    className: styles['success-icon'],
    color: "positive",
    label: false
  }), image && /*#__PURE__*/React.createElement(Image, {
    width: 64,
    height: 64,
    src: image,
    alt: ""
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['text-block']
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['course-title'], 'ud-heading-sm')
  }, title), instructors && /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['instructor-list'], 'ud-text-xs')
  }, instructors.join(', '))), /*#__PURE__*/React.createElement(Button, {
    onClick: onClick,
    "data-purpose": "go-to-cart-button",
    className: styles['go-to-cart']
  }, gettext('Go to cart'))));
};
export const RelatedLabelsList = observer(({
  courseId,
  sourcePage
}) => {
  var _relatedLabelsStore$l;
  const {
    gettext
  } = useI18n();
  const [relatedLabelsStore] = React.useState(() => new RelatedLabelsStore(courseId, sourcePage));
  useEffect(() => {
    relatedLabelsStore.fetchLabels();
  }, [relatedLabelsStore]);
  if (relatedLabelsStore.loading || !((_relatedLabelsStore$l = relatedLabelsStore.labels) !== null && _relatedLabelsStore$l !== void 0 && _relatedLabelsStore$l.length)) {
    return null;
  }
  const renderRelatedLabelItem = ({
    index,
    id,
    display_name,
    url
  }) => {
    const trackClick = () => {
      Tracker.publishEvent(new BrowseNavigationItemSelected({
        id: String(id),
        type: BrowseNavigationItemType.TOPIC,
        uiRegion: 'frequently_bought_together',
        style: BrowseNavigationItemStyle.PILL_LIGHT
      }));
    };
    return /*#__PURE__*/React.createElement(LinkGroup.Item, {
      key: index,
      href: url,
      "data-purpose": "related-link-tag",
      size: "medium",
      onClick: trackClick
    }, display_name);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "ud-heading-lg"
  }, gettext('Related topics')), /*#__PURE__*/React.createElement(LinkGroup, {
    ariaLabel: gettext('Related topics'),
    className: styles['related-labels']
  }, relatedLabelsStore.labels.map((label, index) => renderRelatedLabelItem({
    ...label,
    index
  }))));
});
const InternalCartSuccessModal = observer(({
  buyable,
  className,
  isOpen,
  message,
  showBundleUnit = true,
  showCodingExercisesBadge,
  showRelatedLabelsList = true,
  onClose,
  renderBundleUnit,
  renderSuccessHeader
}) => {
  var _buyable$visible_inst, _buyable$title;
  const {
    gettext
  } = useI18n();
  const goToCart = () => {
    serverOrClient.global.location.href = urls.cartPage;
  };
  const instructorNames = ((_buyable$visible_inst = buyable.visible_instructors) !== null && _buyable$visible_inst !== void 0 ? _buyable$visible_inst : []).map(instructor => instructor.title);
  const DefaultSuccessHeader = /*#__PURE__*/React.createElement(CourseAddedMessage, {
    onClick: goToCart,
    title: (_buyable$title = buyable.title) !== null && _buyable$title !== void 0 ? _buyable$title : '',
    instructors: instructorNames,
    image: buyable.image_100x100
  });
  return /*#__PURE__*/React.createElement(Modal, {
    className: classNames(className, styles['cart-success-modal-dialog']),
    isOpen: isOpen,
    onClose: onClose,
    title: gettext('Added to cart')
  }, /*#__PURE__*/React.createElement("div", {
    className: "cart-success-modal",
    "data-purpose": "cart-success-modal"
  }, message && /*#__PURE__*/React.createElement(AlertBanner, {
    showIcon: false,
    showCta: false,
    className: styles['notice'],
    title: message.title,
    body: message.subtitle
  }), renderSuccessHeader ? renderSuccessHeader() : DefaultSuccessHeader, showBundleUnit && renderBundleUnit && /*#__PURE__*/React.createElement("div", {
    className: classNames({
      [styles['bundle-container']]: showRelatedLabelsList
    })
  }, /*#__PURE__*/React.createElement(FunnelLogContextProvider, {
    context: "cartSuccessModal"
  }, /*#__PURE__*/React.createElement(Provider, {
    showCodingExercisesBadge: showCodingExercisesBadge
  }, /*#__PURE__*/React.createElement(ContentArea, {
    className: styles['bundle-content-area']
  }, /*#__PURE__*/React.createElement(ContentGrid, null, /*#__PURE__*/React.createElement(ContentGridItemFullWidth, null, renderBundleUnit([buyable]))))))), showRelatedLabelsList && buyable.id ? /*#__PURE__*/React.createElement(RelatedLabelsList, {
    courseId: buyable.id,
    sourcePage: "success_page"
  }) : null));
});
export const CartSuccessModal = props => /*#__PURE__*/React.createElement(BrowseCourseCardContext.Provider, {
  value: {
    cardComponent: BrowseCourseCard.defaultCardComponent
  }
}, /*#__PURE__*/React.createElement(InternalCartSuccessModal, props));
//# sourceMappingURL=cart-success-modal.react-component.js.map